import type { FormElementProps } from '@magicschool/business-logic/forms';
import type { ExplicitToolConfig, FileMetadata, InputsType, SafeToolInfo } from '@magicschool/business-logic/tools';
import cloneDeep from 'lodash-es/cloneDeep';
import type { ToolChatRequest } from '@/app/api/generations/tool-chat/route';
import type { LocalizedToolDetailsResponse } from '@/app/api/tools/[slugOrId]/route';
import type { ChatMessage } from '@/features/model-interaction/types';
import { storage } from '../storage';
import { fitFilesInWordLimit } from './fitFilesInWordLimit';

const constructSearchParams = (customizationId?: string, includeAssistantTools?: boolean) => {
  const searchParams = new URLSearchParams();
  if (customizationId) searchParams.append('toolCustomizationId', customizationId);
  if (includeAssistantTools) searchParams.append('includeAssistantTools', 'true');
  return searchParams ? `?${searchParams.toString()}` : '';
};

export async function getTool(
  slug: string,
  customizationId?: string,
  includeAssistantTools?: boolean,
): Promise<LocalizedToolDetailsResponse> {
  const searchParams = constructSearchParams(customizationId, includeAssistantTools);
  const url = `/api/tools/${slug}${searchParams}`;
  const response = await fetch<LocalizedToolDetailsResponse>(url);
  return await response.json();
}

export async function completeToolChat({
  threadId,
  toolDetails,
  message,
  context,
  locale,
  files,
  overrideToolConfig,
  overrideCustomInstructions,
  customizationId,
  webSearchEnabled,
}: {
  threadId: number;
  toolDetails: SafeToolInfo;
  message: string;
  context: ChatMessage[];
  locale: string;
  customizationId: string | undefined;
  files: FileMetadata[] | undefined;
  overrideToolConfig?: ExplicitToolConfig;
  overrideCustomInstructions?: InputsType[string] | undefined;
  webSearchEnabled?: boolean;
}) {
  const request: ToolChatRequest = {
    threadId,
    slug: toolDetails.tool.slug,
    message,
    files: files ? fitFilesInWordLimit(files) : undefined,
    locale,
    context: context as ToolChatRequest['context'], // Changing the function signature type is a big lift, this as allows removal of an any usage.
    overrideToolConfig,
    overrideCustomInstructions,
    customizationId,
    webSearchEnabled,
  };

  return await fetch('/api/generations/tool-chat', { method: 'POST', body: JSON.stringify(request) });
}

export function patchToolReuseValues(toolDetails: SafeToolInfo): SafeToolInfo {
  const fields = patchFieldReuseValues(toolDetails.fields, toolDetails.tool.slug);
  return { ...toolDetails, fields };
}

function patchFieldReuseValues(fields: FormElementProps[], toolId: string) {
  const clonedFields = cloneDeep(fields);
  const savedValues = getSavedToolInputs(toolId) ?? {};
  // Not grade-level-only: `mc-assessment.alignedStandard` also carries `reuseValue`, so this
  // loop restores a prior session's standards text (PEN-244).
  for (const field of clonedFields) {
    if (field.reuseValue) field.initialValue = (savedValues[field.name] as typeof field.initialValue) || field.initialValue;
  }
  const gradeLevelField = clonedFields.find((field: FormElementProps) => field.name === 'gradeLevel');
  if (gradeLevelField) {
    const gradeLevel = storage.getItem(`last-grade-level-selected`);
    if (gradeLevel) {
      gradeLevelField.initialValue = gradeLevel;
    }
  }
  return clonedFields;
}

export const applyGradeLevel = (tool: SafeToolInfo, roomGradeLevel: string) => {
  const gradeLevelField = tool.fields.find((field) => field.name === 'gradeLevel');

  // check if gradeLevel exists in the fields
  if (gradeLevelField) {
    // if the value is different than the room set grade level
    if (gradeLevelField.initialValue !== roomGradeLevel) {
      gradeLevelField.initialValue = roomGradeLevel;
    }

    // we use this in FormBuilder to see if we should override showing it or not
    gradeLevelField.forceHide = true;
  }

  return tool;
};

// TODO: type inputs
export function saveToolReuseValues(inputs: any, toolId: string) {
  storage.setItem(`tool-${toolId}-inputs`, JSON.stringify(inputs));
  if (inputs.gradeLevel) {
    storage.setItem(`last-grade-level-selected`, inputs.gradeLevel);
  }
}

export function getSavedToolInputs(toolId: string): InputsType | undefined {
  const jsonString = storage.getItem(`tool-${toolId}-inputs`);
  if (!jsonString) return undefined;
  try {
    return JSON.parse(jsonString) as InputsType;
  } catch {
    return undefined;
  }
}
